/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import PrintService, {
  RECEIPT_HEADERS,
} from '@linc-technologies/linced-common/services/print';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';
import moment from 'moment';
import countryEnum from '@linc-technologies/linced-common/enums/nz-moe-citizenship';
import EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import ReceiptFormat from '@linc-technologies/linced-common/enums/receipt-format';
import {
  compact,
  filterBy,
  findBy,
  rejectBy,
} from '@linc-technologies/linced-common/utils/arrays';

const downloadAttrSupported = 'download' in document.createElement('a');

// contactPersonIds gives the person ids of the learners' caregivers and
// current medical contacts.
function* contactPersonIds(learners) {
  for (const learner of learners) {
    for (const contact of learner.get('learner.contacts')) {
      yield contact.caregiverId;
    }
    for (const contact of learner.get('learner.currentMedicalContacts')) {
      yield contact.medicalContactId;
    }
  }
}

export default class _PrintService extends PrintService {
  // Services
  @service
  options;

  @service
  paperToaster;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  tax;

  @computed('session.school.jurisdiction')
  get isNzMoe() {
    return this.get('session.school.jurisdiction') === Jurisdiction.NZ_MOE;
  }

  async communityReceipt(transaction, format = ReceiptFormat.STANDARD) {
    let data = await this._generateCommunityReceiptData(
      transaction,
      this.get('session.school'),
      format,
    );
    let jurisdiction = this.get('session.school.jurisdiction');
    let template = this._getReceiptTemplate(jurisdiction, format, true);
    this.download('fromTemplate', data, 'receipt.docx', null, template);
  }

  // private function override until we can work out differences between apps
  // app pushes in a null for accounts which fails on the common function.
  _getEldestSiblingAccount(account, accounts) {
    // account is generally for the most recent transaction in list
    // not necessarily the one we are generating the receipt through
    if (
      !(
        account.get('person.learner.isEldest') &&
        account.get('person.learner.enrolmentStub.status') !==
          EnrolmentStatus.FORMER
      ) &&
      !isEmpty(accounts)
    ) {
      // Resolve all sibling Person promises to ensure that we can find the eldest properly.
      let promises = [];
      account.get('person.learner.currentSiblings').forEach((sibling) => {
        promises.push(sibling.get('person'));
      });
      return Promise.all(promises).then(() => {
        // Find eldest non-former student.
        let eldest = account
          .get('person.learner.currentSiblings')
          .find((sibling) => {
            let enrolmentStatus = sibling.get(
              'person.learner.enrolmentStub.status',
            );
            return enrolmentStatus !== EnrolmentStatus.FORMER;
          });

        // If we have an account for the eldest sibling, use it.
        if (!isEmpty(eldest)) {
          let eldestAccount = accounts.find(
            (a) => a.person_id === eldest.get('personId'),
          );
          if (!isEmpty(eldestAccount)) {
            return eldestAccount;
          }
        }
        return account;
      });
    }
    return Promise.resolve(account);
  }

  async _generateCommunityReceiptData(
    transaction,
    school,
    format = ReceiptFormat.STANDARD,
  ) {
    let placeholders = await this._corePlaceholders(
      null,
      false,
      false,
      '',
      school,
    );

    let items = []; // Non-donation items
    let donations = [];
    let itemTotal = 0;
    let donationTotal = 0;
    let gstTotal = 0;

    // Avoid lexical declarations within the switch cases, to shut eslint up.
    let item, amount, isDonation, row;

    let paymentDetails = transaction.displayMethod;
    if (!isEmpty(transaction.paidBy) && !isEmpty(transaction.paidBy.trim())) {
      paymentDetails += `\nPaid by: ${transaction.paidBy.trim()}`;
    }
    switch (transaction.type) {
      case TransactionType.REFUND:
        item = 'Payment in Advance';
        amount = transaction.amount;
        isDonation = false;
        if (!isEmpty(transaction.line_item_id)) {
          item = transaction.get('lineItem.name');
          amount = transaction.displayAmount;
          isDonation = transaction.get('lineItem.isDonation');
        }

        row = [
          transaction.date,
          transaction.id,
          '',
          item,
          paymentDetails,
          '$' + amount.toFixed(2),
        ];

        if (isDonation) {
          donations.push(row);
          donationTotal += amount;
        } else {
          items.push(row);
          itemTotal += amount;
        }
        break;
    }

    transaction.childTransactions.forEach((txn) => {
      let data = this._receiptTransaction(txn, paymentDetails);

      if (txn.get('lineItem.isDonation')) {
        donations.push(data.row);
        donationTotal += data.amount;
      } else {
        items.push(data.row);
        itemTotal += data.amount;
      }

      gstTotal += txn.get('taxAmount');
    });

    if (
      !isEmpty(school) &&
      school.jurisdiction === Jurisdiction.AU_NSW &&
      format === ReceiptFormat.EIGHTYMM
    ) {
      // Get till code, receiptNo (transaction id) and payment method.
      placeholders.push({
        placeholder: 'RECEIPT_NO',
        value: transaction.get('id'),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TIME',
        value: moment.unix(transaction.get('createTime')).format('HH:mm'),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TILL',
        value: transaction.get('tillSession.till.code'),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'PAYMENT_METHOD',
        value: transaction.get('displayMethod'),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'PAID_BY',
        value: transaction.paidBy || 'Unknown Person',
        type: 'text',
      });

      placeholders.push({
        placeholder: 'GST_TOTAL',
        value: gstTotal.toFixed(2),
        type: 'text',
      });

      placeholders.push({
        placeholder: 'TOTAL',
        value: (itemTotal + donationTotal).toFixed(2),
        type: 'text',
      });
    }

    placeholders.push({
      placeholder: 'BLURB',
      value: this._getReceiptBlurb(school),
      type: 'text',
    });

    let signature = await this._getElectronicSignature(school);
    if (!isEmpty(signature)) {
      placeholders.push({
        placeholder: 'ELECTRONICSIGNATURE',
        value: signature,
        type: 'html',
      });
    }

    if (!isEmpty(items)) {
      placeholders.push(
        this._itemsTable(
          items,
          itemTotal,
          RECEIPT_HEADERS,
          school.jurisdiction,
          format,
          gstTotal,
        ),
      );
    }

    if (!isEmpty(donations)) {
      placeholders.push(
        this._donationsTable(
          donations,
          donationTotal,
          RECEIPT_HEADERS,
          school.jurisdiction,
          format,
        ),
      );
    }

    let data = this._reducePlaceholders(placeholders);

    data.overridetemplatesymbol = true;

    return data;
  }

  async _generateLineItemReceiptData(lineItem, transactions) {
    let account = lineItem.get('account');
    let learner = account.get('person');
    let placeholders = await this._corePlaceholders(
      learner,
      true,
      true,
      'Student',
    );

    // Non-donation items
    let items = [];
    let donations = [];
    let itemTotal = 0;
    let donationTotal = 0;

    transactions.forEach((txn) => {
      let paymentDetails = txn.get('displayMethod');
      if (!isEmpty(txn.paidBy) && !isEmpty(txn.paidBy.trim())) {
        paymentDetails += `\nPaid by: ${txn.paidBy.trim()}`;
      }
      let data = this._receiptTransaction(txn, paymentDetails);

      if (txn.get('lineItem.isDonation')) {
        donations.push(data.row);
        donationTotal += data.amount;
      } else {
        items.push(data.row);
        itemTotal += data.amount;
      }
    });

    placeholders.push({
      placeholder: 'BLURB',
      value: this.options.value('finance:school:receiptBlurb'),
      type: 'text',
    });

    placeholders.push(...this._parentAddressPlaceholders(account));

    if (!isEmpty(items)) {
      placeholders.push(this._itemsTable(items, itemTotal, RECEIPT_HEADERS));
    }

    if (!isEmpty(donations)) {
      placeholders.push(
        this._donationsTable(donations, donationTotal, RECEIPT_HEADERS),
      );
    }

    let data = this._reducePlaceholders(placeholders);

    data.overridetemplatesymbol = true;

    return data;
  }

  async lineItemReceipt(lineItem, transactions) {
    let data = await this._generateLineItemReceiptData(lineItem, transactions);
    let jurisdiction = this.get('session.school.jurisdiction');
    this.download(
      'fromTemplate',
      data,
      'receipt.docx',
      null,
      this._getReceiptTemplate(jurisdiction),
    );
  }

  async _generateStatementData(
    lineItems,
    learner,
    accounts,
    template,
    options,
  ) {
    let includeLearnerName = options.includeLearnerName || false;
    let includeHomeroomName = options.includeHomeroomName || false;
    let placeholders = await this._corePlaceholders(
      learner,
      includeLearnerName,
      includeHomeroomName,
    );

    // Table headers
    const headers = [
      'Date',
      'Student Name',
      'Activity / Description',
      'Ref No',
      'Debit',
      'Credits',
      'Balance',
    ];
    const headersWithTax = [
      'Date',
      'Student Name',
      'Activity / Description',
      'Ref No',
      'Debit',
      this.tax.taxLabel,
      'Credits',
      'Balance',
    ];
    let items = [];
    let donations = [];
    let itemBalance = 0;
    let donationBalance = 0;
    let remittance = [['Section', 'Balance']];

    // Generate data tables
    lineItems.forEach((item) => {
      if (item.isArchived) {
        // Skip archived items.
        return;
      }

      let itemName = item.name;
      if (!isEmpty(item.description)) {
        itemName += `\n${item.description}`;
      }
      let row = [
        item.date,
        item.get('account.person.fullName'),
        itemName,
        item.id,
        '$' + Number(item.displayAmount).toFixed(2),
        '$' + Number(item.credit).toFixed(2),
        '$' + Number(item.balance).toFixed(2),
      ];
      if (item.isDonation) {
        donations.push(row);
        donationBalance += item.balance;
      } else {
        if (this.showTaxOnStatements) {
          let tax = item.get('taxAmount');
          if (!isEmpty(tax)) {
            tax = `$${tax.toFixed(2)}`;
          }
          row.splice(5, 0, tax);
        }
        items.push(row);
        itemBalance += item.balance;
      }
    });

    accounts.forEach((account) => {
      if (account.balance > 0) {
        let piaRow = [
          '',
          account.get('person.fullName'),
          'Payment in advance',
          '',
          '',
          '$' + account.balance.toFixed(2),
          '$' + (-1 * account.balance).toFixed(2),
        ];
        if (this.showTaxOnStatements) {
          piaRow.splice(5, 0, '');
        }
        items.push(piaRow);
        itemBalance -= account.balance;
      }
    });

    if (!isEmpty(items)) {
      if (this.showTaxOnStatements) {
        items.unshift(headersWithTax);
        items.push(['', '', '', '', '', '', '', '']);
        items.push([
          'Total',
          '',
          '',
          '',
          '',
          '',
          '',
          '$' + itemBalance.toFixed(2),
        ]);
      } else {
        items.unshift(headers);
        items.push(['', '', '', '', '', '', '']);
        items.push(['Total', '', '', '', '', '', '$' + itemBalance.toFixed(2)]);
      }
      placeholders.push({
        placeholder: 'TABLE',
        value: items,
        type: 'table',
        label: 'Non-Donation Line Items',
      });
      remittance.push(['Non-Donations', '$' + itemBalance.toFixed(2)]);
    }
    if (!isEmpty(donations)) {
      donations.unshift(headers);
      donations.push(['', '', '', '', '', '', '']);
      donations.push([
        'Total',
        '',
        '',
        '',
        '',
        '',
        '$' + donationBalance.toFixed(2),
      ]);
      placeholders.push({
        placeholder: 'DONATIONSTABLE',
        value: donations,
        type: 'table',
        label: 'Donation Line Items',
      });
      remittance.push(['Donations', '$' + donationBalance.toFixed(2)]);
    }
    remittance.push([
      'TOTAL',
      '$' + (donationBalance + itemBalance).toFixed(2),
    ]);

    // Add remittance advice table
    placeholders.push({
      placeholder: 'REMITTENCETABLE', // sic
      value: remittance,
      type: 'table',
    });

    // Blurb lines are also shared across statements.
    placeholders.push({
      placeholder: 'BLURB',
      value: this.options.value('finance:school:statementBlurb'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'BLURB2',
      value: this.options.value('finance:school:statementBlurb2'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'BLURB3',
      value: this.options.value('finance:school:statementBlurb3'),
      type: 'text',
    });

    if (!isEmpty(this.options.value('finance:school:bankAccount'))) {
      placeholders.push({
        placeholder: 'BANK',
        value: 'Bank a/c: ' + this.options.value('finance:school:bankAccount'),
        type: 'text',
      });
    }

    if (!isEmpty(this.options.value('finance:school:bankName'))) {
      placeholders.push({
        placeholder: 'ACCOUNTNAME',
        value: 'Payee Name: ' + this.options.value('finance:school:bankName'),
        type: 'text',
      });
    }

    // TODO: Support multiple/split accounts.  For now, use first account for primary learner.
    let account = accounts.find((a) => a.person_id === learner.id);

    placeholders.push(...this._parentAddressPlaceholders(account, accounts));

    let data = this._reducePlaceholders(placeholders);

    data.overridetemplatesymbol = true;
    data.template = template;

    return data;
  }

  async statement(lineItems, learner, accounts, onComplete, options) {
    let data = await this._generateStatementData(
      lineItems,
      learner,
      accounts,
      'statement.docx',
      options,
    );
    this.download(
      'fromTemplate',
      data,
      'statement.docx',
      onComplete,
      'statement.docx',
    );
  }

  // loadContacts loads the caregivers and the current medical contacts of the
  // learners, so that the contact placeholders can read them from the store.
  loadContacts(learners) {
    return loadPeople(this.store, contactPersonIds(learners));
  }

  _addContactPlaceholders(learner, placeholders) {
    let careGiverNotes = '';
    learner.get('learner.primaryContacts').forEach((contact, index) => {
      if (!isEmpty(contact)) {
        let contactCount = index + 1;
        let caregiver = this.store.peekRecord(
          'person',
          contact.get('caregiverId'),
        );
        if (isEmpty(caregiver)) {
          // If we don't have the caregiver's Person record, skip them.
          return;
        }
        let notes = contact.get('notes');
        if (!isEmpty(notes)) {
          careGiverNotes +=
            caregiver.get('fields.core:firstName.value') + ':' + notes + '\n';
        }

        placeholders.push({
          placeholder: `CONTACT_${contactCount}_FIRST_NAME`,
          value: caregiver.get('fields.core:firstName.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_LAST_NAME`,
          value: caregiver.get('fields.core:lastName.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_TYPE`,
          value: contact.get('relationshipName'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_COMMENT`,
          value: contact.get('notes'),
          type: 'text',
        });

        let prefix = 'core';
        if (this.isNzMoe) {
          prefix = 'nzMOE';
        }
        [1, 2, 3].forEach((lineNo) => {
          placeholders.push({
            placeholder: `CONTACT_${contactCount}_ADDRESS${lineNo}`,
            value: caregiver.get(`fields.${prefix}:address${lineNo}.value`),
            type: 'text',
          });
        });

        let address4 = caregiver.get(`fields.${prefix}:address4.value`);
        let postcode = caregiver.get(`fields.${prefix}:postcode.value`);
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_ADDRESS4`,
          value: compact([address4, postcode]).join(' '),
          type: 'text',
        });

        placeholders.push({
          placeholder: `CONTACT_${contactCount}_EMAIL`,
          value: caregiver.get('fields.core:emailAddress.value'),
          type: 'text',
        });

        placeholders.push({
          placeholder: `CONTACT_${contactCount}_HOME`,
          value: caregiver.get('fields.core:phoneHome.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_MOBILE`,
          value: caregiver.get('fields.core:phoneMobile.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `CONTACT_${contactCount}_WORK_PHO`,
          value: caregiver.get('fields.core:phoneWork.value'),
          type: 'text',
        });
      }
    });

    // Emergency Contacts
    learner.get('learner.additionalContacts').forEach((contact, index) => {
      if (!isEmpty(contact)) {
        let contactCount = index + 1;
        let caregiver = this.store.peekRecord(
          'person',
          contact.get('caregiverId'),
        );
        if (isEmpty(caregiver)) {
          // If we don't have the caregiver's Person record, skip them.
          return;
        }
        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_FIRST`,
          value: caregiver.get('fields.core:firstName.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_LAST`,
          value: caregiver.get('fields.core:lastName.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_COMMENT`,
          value: contact.get('notes'),
          type: 'text',
        });

        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_PHONE`,
          value: caregiver.get('fields.core:phoneHome.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_MOBILE`,
          value: caregiver.get('fields.core:phoneMobile.value'),
          type: 'text',
        });
        placeholders.push({
          placeholder: `EMERGENCY${contactCount}_WORK`,
          value: caregiver.get('fields.core:phoneWork.value'),
          type: 'text',
        });
      }
    });

    //Access Arrangements
    placeholders.push({
      placeholder: 'ACCESS_ARRANGEMENTS',
      value: learner.get('fields.core:accessArrangements.value'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CAREGIVER_COMMENT',
      value: careGiverNotes,
      type: 'text',
    });
  }

  _addMedicalContactPlaceholders(currentMedicalContacts, placeholders) {
    currentMedicalContacts.forEach((contact, index) => {
      if (!isEmpty(contact) && contact.get('isCurrent')) {
        let contactCount = index + 1;
        let medicalPerson = this.store.peekRecord(
          'person',
          contact.get('medicalContactId'),
        );
        if (!isEmpty(medicalPerson)) {
          placeholders.push({
            placeholder: `MEDICAL${contactCount}_TYPE`,
            value: contact.relationshipName,
            type: 'text',
          });
          placeholders.push({
            placeholder: `MEDICAL${contactCount}_FIRST`,
            value: medicalPerson.get('fields.core:firstName.value'),
            type: 'text',
          });
          placeholders.push({
            placeholder: `MEDICAL${contactCount}_LAST`,
            value: medicalPerson.get('fields.core:lastName.value'),
            type: 'text',
          });
          placeholders.push({
            placeholder: `MEDICAL_CENTRE${contactCount}`,
            value: medicalPerson.get('fields.core:medicalCentre.value'),
            type: 'text',
          });
          placeholders.push({
            placeholder: `MEDICAL_PHONE${contactCount}`,
            value: medicalPerson.get('fields.core:workPhoneNumber.value'),
            type: 'text',
          });
        }
      }
    });
  }

  _addMedicalFieldPlaceholders(learner, placeholders) {
    let doctor = learner.get('fields.core:doctor.value') ?? '';
    let doctorLabel = 'Doctor:';
    let currentMedicalContacts = learner.get('learner.currentMedicalContacts');
    if (isEmpty(doctor.trim()) && !isEmpty(currentMedicalContacts)) {
      // Don't show legacy "Doctor:" label if there's no value and at least one
      // current medical contact will be shown.
      doctorLabel = '';
    }
    let currentMedicalConditions = learner.get(
      'learner.currentMedicalConditions',
    );
    let condition = learner.get('fields.core:medicalCondition.value');
    let conditionLabel = '';
    let allergy = learner.get('fields.core:allergies.value');
    let allergyLabel = '';
    let treatment = learner.get('fields.core:medicalTreatment.value');
    let treatmentLabel = '';
    if (!isEmpty(currentMedicalConditions)) {
      // Show labels if we have new-style conditions in the mix AND the
      // corresponding field is non-empty.
      if (!isEmpty(condition)) {
        conditionLabel = 'Other:';
      }
      if (!isEmpty(allergy)) {
        allergyLabel = 'Allergy:';
      }
      if (!isEmpty(treatment)) {
        treatmentLabel = 'Treatment:';
      }
    }
    placeholders.push({
      placeholder: 'DOCTOR',
      value: doctor,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'DOCTOR_LABEL',
      value: doctorLabel,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CRITICAL_INFO',
      value: learner.get('fields.core:criticalInfo.value'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'MEDICAL_NOTES',
      value: learner.get('fields.core:medicalNotes.value'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'MEDICAL_CONDITION',
      value: condition,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'MEDICAL_CONDITION_LABEL',
      value: conditionLabel,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'ALLERGY',
      value: allergy,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'ALLERGY_LABEL',
      value: allergyLabel,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'MEDICAL_TREATMENT',
      value: treatment,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'MEDICAL_TREATMENT_LABEL',
      value: treatmentLabel,
      type: 'text',
    });
  }

  _addMedicalConditionPlaceholders(conditions, placeholders) {
    filterBy(conditions, 'isSerious').forEach((condition, index) => {
      let conditionCount = index + 1;
      placeholders.push({
        placeholder: `SERIOUS_CONDITION${conditionCount}_TYPE`,
        value: condition.typeName,
        type: 'text',
      });
      placeholders.push({
        placeholder: `SERIOUS_CONDITION${conditionCount}_SUBTYPE`,
        value: condition.get('fields.core:subtype.value'),
        type: 'text',
      });
      placeholders.push({
        placeholder: `SERIOUS_CONDITION${conditionCount}_SUMMARY`,
        value: condition.summary,
        type: 'text',
      });
      placeholders.push({
        placeholder: `SERIOUS_CONDITION${conditionCount}_DATES`,
        value: condition.dates,
        type: 'text',
      });
    });
    rejectBy(conditions, 'isSerious').forEach((condition, index) => {
      let conditionCount = index + 1;
      placeholders.push({
        placeholder: `NONSERIOUS_CONDITION${conditionCount}_TYPE`,
        value: condition.typeName,
        type: 'text',
      });
      placeholders.push({
        placeholder: `NONSERIOUS_CONDITION${conditionCount}_SUBTYPE`,
        value: condition.get('fields.core:subtype.value'),
        type: 'text',
      });
      placeholders.push({
        placeholder: `NONSERIOUS_CONDITION${conditionCount}_SUMMARY`,
        value: condition.summary,
        type: 'text',
      });
      placeholders.push({
        placeholder: `NONSERIOUS_CONDITION${conditionCount}_DATES`,
        value: condition.dates,
        type: 'text',
      });
    });
  }

  profile(learner, onComplete) {
    let placeholders = [];
    let firstName = learner.get('fields.core:firstNameLegal.valueString');
    let preferredFirstName = learner.get(
      'fields.core:firstNamePreferred.valueString',
    );
    if (!isEmpty(preferredFirstName) && preferredFirstName !== firstName) {
      firstName = `${preferredFirstName} (${firstName})`;
    }
    // Student Info
    placeholders.push({
      placeholder: 'PREFERRED_NAME',
      value: firstName,
      type: 'text',
    });
    let lastName = learner.get('fields.core:lastNameLegal.valueString');
    let preferredLastName = learner.get(
      'fields.core:lastNamePreferred.valueString',
    );
    if (!isEmpty(preferredLastName) && preferredLastName !== lastName) {
      lastName = `${preferredLastName} (${lastName})`;
    }
    placeholders.push({
      placeholder: 'PREFERRED_LAST_NAME',
      value: lastName,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CLASS',
      value: learner.get('homeroom.label'),
      type: 'text',
    });

    // Medical Info
    this._addMedicalFieldPlaceholders(learner, placeholders);
    placeholders.push({
      placeholder: 'PERMISSION_ALERT',
      value: learner.get('fields.core:permissionAlert.value'),
      type: 'text',
    });

    // Add medical conditions.
    this._addMedicalConditionPlaceholders(
      learner.get('learner.currentMedicalConditions'),
      placeholders,
    );

    // Contacts - wait for all promises to resolve so that we have their
    // person records available.
    this.loadContacts([learner]).then(() => {
      this._addContactPlaceholders(learner, placeholders);
      this._addMedicalContactPlaceholders(
        learner.get('learner.currentMedicalContacts'),
        placeholders,
      );

      placeholders.push({
        placeholder: 'RESIDENTIAL_ADDRESS',
        value: learner.get('address').join(', '),
        type: 'text',
      });

      let data = this._reducePlaceholders(placeholders);

      this.download(
        'fromTemplate',
        data,
        `Profile ${firstName} ${lastName}.docx`,
        onComplete,
        'profile.docx',
      );
    });
  }

  async medicalSheet(learner, onComplete) {
    let firstName = learner.get('fields.core:firstNamePreferred.valueString');
    let legalFirstName = learner.get('fields.core:firstNameLegal.valueString');
    let lastName = learner.get('fields.core:lastNamePreferred.valueString');
    let legalLastName = learner.get('fields.core:lastNameLegal.valueString');

    let placeholders = this._buildCommonMedicalPlaceholders(learner);

    await this._finalizeCommonAsync(learner, placeholders);
    await this._appendJurisdictionPlaceholders(learner, placeholders);

    let template = 'emergency_international.docx';
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.NZ_MOE:
        template = 'emergency.docx';
        break;
      case Jurisdiction.AU_NSW:
        template = 'emergency_au_nsw.docx';
        break;
    }

    this.download(
      'fromTemplate',
      this._reducePlaceholders(placeholders),
      `Medical_Sheet ${firstName || legalFirstName} ${
        lastName || legalLastName
      }.docx`,
      onComplete,
      template,
    );
  }

  async medicalCentreReport(learner, sickbayVisit, onComplete) {
    let firstName = learner.get('fields.core:firstNamePreferred.valueString');
    let legalFirstName = learner.get('fields.core:firstNameLegal.valueString');
    let lastName = learner.get('fields.core:lastNamePreferred.valueString');
    let legalLastName = learner.get('fields.core:lastNameLegal.valueString');

    let placeholders = this._buildCommonMedicalPlaceholders(learner);

    await this._finalizeCommonAsync(learner, placeholders);
    await this._appendJurisdictionPlaceholders(learner, placeholders);

    this._addMedicalCentreExtras(sickbayVisit, placeholders);

    // choose template with jurisdiction suffix
    let template = 'medical_centre_international.docx';
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.NZ_MOE:
        template = 'medical_centre.docx';
        break;
      case Jurisdiction.AU_NSW:
        template = 'medical_centre_au_nsw.docx';
        break;
    }

    this.download(
      'fromTemplate',
      this._reducePlaceholders(placeholders),
      `Medical_Centre ${firstName || legalFirstName} ${
        lastName || legalLastName
      }.docx`,
      onComplete,
      template,
    );
  }

  _buildCommonMedicalPlaceholders(learner) {
    let placeholders = [];

    let firstName = learner.get('fields.core:firstNamePreferred.valueString');
    let legalFirstName = learner.get('fields.core:firstNameLegal.valueString');
    let lastName = learner.get('fields.core:lastNamePreferred.valueString');
    let legalLastName = learner.get('fields.core:lastNameLegal.valueString');

    // Student Info
    placeholders.push({
      placeholder: 'PREFERRED_NAME',
      value: firstName,
      type: 'text',
    });
    if (legalFirstName !== firstName) {
      placeholders.push({
        placeholder: 'LEGAL_NAME',
        value: legalFirstName,
        type: 'text',
      });
    }
    if (legalLastName !== lastName) {
      placeholders.push({
        placeholder: 'LEGAL_LAST_NAME',
        value: legalLastName,
        type: 'text',
      });
    }
    placeholders.push({
      placeholder: 'PREFERRED_LAST_NAME',
      value: lastName,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CLASS',
      value: learner.get('homeroom.label'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'PERMISSION_ALERT',
      value: learner.get('fields.core:permissionAlert.value'),
      type: 'text',
    });

    // Medical Info
    this._addMedicalFieldPlaceholders(learner, placeholders);
    placeholders.push({
      placeholder: 'IMMUNISATIONS',
      value: learner.get('fields.core:immunisations.value'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'IMMUNISATION_COM',
      value: learner.get('fields.core:immunisationComment.value'),
      type: 'text',
    });
    let firstaidPermission = learner.get(
      'fields.core:firstaidPermission.value',
    );
    if (!isEmpty(firstaidPermission)) {
      placeholders.push({
        placeholder: 'FIRSTAIDPERMISSION',
        value: firstaidPermission,
        type: 'text',
      });
    }
    let panadolPermission = learner.get('fields.core:panadolPermission.value');
    if (!isEmpty(panadolPermission)) {
      placeholders.push({
        placeholder: 'PANADOLPERMISSION',
        value: panadolPermission,
        type: 'text',
      });
    }
    let healthNurse = learner.get('fields.core:healthNurse.value');
    if (!isEmpty(healthNurse)) {
      placeholders.push({
        placeholder: 'HEALTH_NURSE',
        value: healthNurse,
        type: 'text',
      });
    }

    // Add medical conditions.
    this._addMedicalConditionPlaceholders(
      learner.get('learner.currentMedicalConditions'),
      placeholders,
    );

    return placeholders;
  }

  async _finalizeCommonAsync(learner, placeholders) {
    await this.loadContacts([learner]);

    this._addContactPlaceholders(learner, placeholders);
    this._addMedicalContactPlaceholders(
      learner.get('learner.currentMedicalContacts'),
      placeholders,
    );

    let prefix = 'core';
    if (this.isNzMoe) {
      prefix = 'nzMOE';
    }
    placeholders.push({
      placeholder: 'nzMOE:ADDRESS1',
      value: learner.get(`fields.${prefix}:address1.value`) ?? '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'nzMOE:ADDRESS2',
      value: learner.get(`fields.${prefix}:address2.value`) ?? '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'nzMOE:ADDRESS3',
      value: learner.get(`fields.${prefix}:address3.value`) ?? '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'nzMOE:ADDRESS4',
      value: learner.get(`fields.${prefix}:address4.value`) ?? '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CORE:STUDENTHOMEPHONE',
      value: learner.get('fields.core:studenthomePhone.value'),
      type: 'text',
    });

    // Additional fields
    placeholders.push({
      placeholder: 'DATE',
      value: moment(Date.now()).format('YYYY-MM-DD'),
      type: 'text',
    });

    placeholders.push({
      placeholder: 'NSN',
      value: learner.jurisdictionalId,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'DOB',
      value: learner.get('fields.core:dob.value'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'CURRENT_YEAR_LEVEL',
      value: learner.get('currentYearLevel'),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'BIRTH_COUNTRY',
      value: learner.get(`fields.${prefix}:countryBirth.value`),
      type: 'text',
    });
    placeholders.push({
      placeholder: 'FIRST_LANGUAGE',
      value: learner.get(`fields.${prefix}:homeLanguage.value`),
      type: 'text',
    });

    return placeholders;
  }

  async _appendJurisdictionPlaceholders(learner, placeholders) {
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.NZ_MOE:
        placeholders.push({
          placeholder: 'CITIZENSHIP',
          value: (
            Object.keys(countryEnum).find(function (key) {
              return (
                countryEnum[key] ===
                learner.get('fields.nzMOE:countryCitizenship.value')
              );
            }) || ''
          ).replace('_', ' '),
          type: 'text',
        });
        break;

      case Jurisdiction.AU_NSW: {
        let field = await this.schoolSchema.getField(
          PersonType.LEARNER,
          'core:residency',
        );
        if (!isEmpty(field) && !isEmpty(field.options)) {
          let option = findBy(
            field.options,
            'value',
            learner.get('fields.core:residency.value'),
          );
          if (!isEmpty(option)) {
            placeholders.push({
              placeholder: 'RESIDENCY',
              value: option.label,
              type: 'text',
            });
          }
        }
        placeholders.push({
          placeholder: 'DOCTOR_PHONE',
          value: learner.get('fields.core:doctorPhone.value'),
          type: 'text',
        });
        break;
      }

      default:
        break;
    }
    return placeholders;
  }

  _addMedicalCentreExtras(sickbayVisit, placeholders) {
    placeholders.push({
      placeholder: 'ENTRY_DATE',
      value: sickbayVisit.date,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'ENTRY_TIME',
      value: sickbayVisit.checkInTimeString,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'RELEASE_TIME',
      value: sickbayVisit.checkOutTimeString,
      type: 'text',
    });
    placeholders.push({
      placeholder: 'ENTRY_NOTE',
      value: sickbayVisit.notes,
      type: 'text',
    });
    if (!isEmpty(sickbayVisit.details)) {
      placeholders.push({
        placeholder: 'ENTRY_DETAILS',
        value: `<div style="font-size:12px">${sickbayVisit.details}</div>`,
        type: 'html',
      });
    }
    placeholders.push({
      placeholder: 'RELEASE_NOTE',
      value: sickbayVisit.releaseNotes,
      type: 'text',
    });
    if (!isEmpty(sickbayVisit.releaseDetails)) {
      placeholders.push({
        placeholder: 'RELEASE_DETAILS',
        value: `<div style="font-size:12px">${sickbayVisit.releaseDetails}</div>`,
        type: 'html',
      });
    }
    placeholders.push({
      placeholder: 'CHECKED_OUT_NAME',
      value: sickbayVisit.checkedOutBy
        ? sickbayVisit.checkedOutBy.get('fullName')
        : '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'SCHOOL_NAME',
      value: sickbayVisit.school ? sickbayVisit.school.get('name') : '',
      type: 'text',
    });
    placeholders.push({
      placeholder: 'SCHOOL_PH_NUMBER',
      value: sickbayVisit.school ? sickbayVisit.school.get('phone') : '',
      type: 'text',
    });

    return placeholders;
  }

  downloadRawCsv(fileName, content) {
    if (downloadAttrSupported) {
      let link = this._getLink();
      link.href = `data:text/csv,${content}`;
      if (!isEmpty(fileName)) {
        link.download = cleanFileName(fileName);
      } else {
        link.download = 'data.csv';
      }
      link.click();
    } else {
      let frame = this._getFrame();
      frame.src = `data:text/csv,${content}`;
    }
  }
}
